import React, { useCallback } from "react";

import { isArray } from "lodash-es";

import Region from "@mapmycustomers/shared/types/Region";
import defaultFilterOption from "@mapmycustomers/shared/util/input/defaultFilterOption";
import useRegionsListOptions from "@mapmycustomers/shared/util/regions/useRegionsListOptions";

import { useConfigProvider } from "../../../../ConfigProvider";
import InlineTextField from "../../../inline/InlineTextField";
import InlineSelectField, { InlineSelectFieldProps } from "../../../inline/SelectField";

interface RegionFieldProps {
  countryCode?: string;
  disabled?: boolean;
  editing?: boolean;
  onChange?: (region: Region) => void;
  onEditingChange?: (editing: boolean) => void;
  value?: Region;
}

type InlineSelectFieldValueType = string | undefined;

const RegionField = ({
  countryCode,
  disabled,
  editing,
  onChange,
  onEditingChange,
  value,
}: RegionFieldProps) => {
  const configProvider = useConfigProvider();
  const regions = useRegionsListOptions(countryCode);
  const hasRegions = regions?.length > 0;

  const handleChange = useCallback<
    NonNullable<InlineSelectFieldProps<InlineSelectFieldValueType>["onChange"]>
  >(
    (value, item) => {
      onChange?.({
        region: value !== undefined && !isArray(item) ? (item.label as string) : undefined,
        regionCode: value,
      });
    },
    [onChange]
  );

  const handleTextChange = useCallback(
    (value: string) => {
      onChange?.({
        region: value.length > 0 ? value : undefined,
        regionCode: undefined,
      });
    },
    [onChange]
  );

  return hasRegions ? (
    <InlineSelectField<string | undefined>
      allowClear
      disabled={disabled}
      dropdownMatchSelectWidth
      editing={editing}
      filterOption={defaultFilterOption}
      onChange={handleChange}
      onEditingChange={onEditingChange}
      options={regions}
      placeholder={configProvider.formatMessage("ui.manualAddress.regionProvince")}
      showFooterButtons={false}
      showSearch
      value={value?.regionCode}
    />
  ) : (
    <InlineTextField
      disabled={disabled}
      editing={editing}
      onChange={handleTextChange}
      onEditingChange={onEditingChange}
      placeholder={configProvider.formatMessage("ui.manualAddress.regionProvince")}
      showFooterButtons={false}
      value={value?.region}
    />
  );
};

export default RegionField;
